import {DataTypeOperator, type FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import {SurveyInvitationStatus} from "crqlar-react-common/dist/models/survey/surveyInvitation.model";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {toInclusiveDayRange} from "./useUrlDateRange";

export const SURVEY_SUBMISSIONS_PATH = '/system/surveySubmissions';
export const SURVEY_INVITATIONS_PATH = '/system/surveyInvitations';

/** Opens one reply on top of either submissions list, wherever it sits in the list or its period. */
export const SURVEY_REPLY_PARAM = 'reply';

/** The server tables' URL filter alias for the Survey filter. */
const SURVEY_ID_PARAM = 'surveyId';

/** The server tables' versioned filter parameter (see common/serverTable/urlState.ts). */
const FILTERS_PARAM = 'filters';
const FILTER_STATE_VERSION = 1;

export interface SurveySubmissionsLinkScope {
    surveyId?: string
}

export interface SurveyInvitationsLinkScope {
    surveyId?: string
    /** Whole days, sent within. */
    range?: DateRange
    /** Every invitation count leaves voided links out, while the list keeps them unless told otherwise. */
    withoutVoided?: boolean
}

const LIVE_INVITATION_STATUSES = Object.values(SurveyInvitationStatus)
    .filter(status => status !== SurveyInvitationStatus.VOIDED);

/*
    Anything a link narrows beyond the survey travels as ordinary filter rules, so the list opens
    with chips that say why it shows what it shows, and the reader can remove them.
 */
const surveyListLink = (path: string, surveyId: string | undefined, rules: FilterRule[] = []) => {
    const params = new URLSearchParams();
    if (surveyId) params.set(SURVEY_ID_PARAM, surveyId);
    if (rules.length) params.set(FILTERS_PARAM, JSON.stringify({version: FILTER_STATE_VERSION, rules}));

    const search = params.toString();
    return search ? `${path}?${search}` : path;
};

export const surveySubmissionsLink = ({surveyId}: SurveySubmissionsLinkScope = {}) =>
    surveyListLink(SURVEY_SUBMISSIONS_PATH, surveyId);

/** One reply, in the module's Submissions tab every reviewer can open; the settings list without the module. */
export const surveyReplyLink = (replyId: string, surveysAppId?: string) => {
    const path = surveysAppId ? `/surveys/submissions/${encodeURIComponent(surveysAppId)}` : SURVEY_SUBMISSIONS_PATH;
    return `${path}?${new URLSearchParams({[SURVEY_REPLY_PARAM]: replyId}).toString()}`;
};

export const surveyInvitationsLink = ({surveyId, range, withoutVoided}: SurveyInvitationsLinkScope = {}) => {
    const rules: FilterRule[] = [];

    if (range) {
        const {start, end} = toInclusiveDayRange(range);
        rules.push({field: 'sentAt', operator: DataTypeOperator.BETWEEN, timestampValues: [start, end]});
    }
    if (withoutVoided) {
        rules.push({field: 'status', operator: DataTypeOperator.IN, textValues: LIVE_INVITATION_STATUSES});
    }

    return surveyListLink(SURVEY_INVITATIONS_PATH, surveyId, rules);
};
